import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  useListFscHouseholdDocumentsQuery,
  useGetMigratedDocumentUrlMutation,
} from '../../app/api';
import { fetchAsBlobUrl } from '../utils';
import DocumentViewerModal from '../../components/DocumentViewerModal';

/* The FSC household's document corpus, as a searchable folder tree.
 *
 * Extracted from FscClientDetail so the hub's own client page can show the
 * same documents once a household is linked to an AUM client - one client,
 * one set of documents, whichever page you came in from. The two pages differ
 * only in the card they wrap this in, so the outer chrome is deliberately left
 * to the caller and only the toolbar, tree and viewer live here.
 *
 * Renders nothing but a message when `fscHouseholdId` is missing, so a caller
 * can mount it unconditionally.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
};

// Grouping for the folder tree.
//
// NOTE: migrated documents have NO folder structure - S3 keys are flat and the
// `documents` table carries only filename/content_type/size/source/created. The
// portal's DocumentBrowser groups by client → category → type, and none of those
// categories exist here; they are produced by the AI classification step, which
// is not built yet.
//
// So the tree groups by what we actually have: source (client vs advisor
// paperwork) and then kind (Notes / PDFs / Spreadsheets / …). When classification
// lands, category simply becomes another level and this function is where it goes.
// An unmapped source falls through to its raw key as the folder name, which
// is how `fsc_upload` had been showing. The three values actually present in
// `documents` today are salentica_migration (46,920), salentica_rep (442) and
// fsc_upload (1); the other two are kept for sources that were planned.
const SOURCE_LABELS = {
  salentica_migration: 'Client documents',
  salentica_rep: 'Advisor & Form ADV',
  fsc_upload: 'Uploaded to Salesforce',
  hub_upload: 'Uploaded via the Hub',
  fsc_capture: 'Captured from Salesforce',
};

function kindOf(doc) {
  const name = (doc.filename || '').toLowerCase();
  if (name.endsWith('.snote')) return 'Notes';
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (ext === 'pdf') return 'PDFs';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'Spreadsheets';
  if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) return 'Documents';
  if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return 'Images';
  return 'Other';
}

function buildTree(documents) {
  const roots = new Map();
  documents.forEach((d) => {
    const sKey = d.source || 'unknown';
    let src = roots.get(sKey);
    if (!src) {
      src = { id: `s:${sKey}`, name: SOURCE_LABELS[sKey] || sKey, kids: new Map(), count: 0 };
      roots.set(sKey, src);
    }
    src.count += 1;

    const kKey = kindOf(d);
    let kind = src.kids.get(kKey);
    if (!kind) {
      kind = { id: `${src.id}/k:${kKey}`, name: kKey, files: [], count: 0 };
      src.kids.set(kKey, kind);
    }
    kind.count += 1;
    kind.files.push(d);
  });

  const out = Array.from(roots.values()).map((s) => ({
    ...s,
    kids: Array.from(s.kids.values())
      .map((k) => ({ ...k, files: k.files.sort((a, b) => (a.filename || '').localeCompare(b.filename || '')) }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }));
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function FscDocumentTree({ fscHouseholdId, isMobile, emptyMessage }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [viewer, setViewer] = useState(null);
  // doc_id currently being fetched. A 1 MB PDF takes a moment across the
  // mint-token → fetch-blob round trip, and without feedback the row looks dead.
  const [loadingDoc, setLoadingDoc] = useState(null);

  const { data: docsData, isLoading: loadingDocs, isError: docsError } = useListFscHouseholdDocumentsQuery(
    { id: fscHouseholdId, limit: 1000 },
    { skip: !fscHouseholdId },
  );
  const [getDocumentUrl] = useGetMigratedDocumentUrlMutation();

  const documents = useMemo(() => docsData?.documents || [], [docsData]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((d) => (d.filename || '').toLowerCase().includes(q));
  }, [documents, query]);

  const tree = useMemo(() => buildTree(filtered), [filtered]);

  // While a search is active every folder is opened, so matches are visible
  // without the operator expanding each branch by hand.
  const searching = query.trim().length > 0;
  const isOpen = useCallback((id) => searching || open.has(id), [searching, open]);
  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allIds = useMemo(() => {
    const ids = [];
    tree.forEach((s) => { ids.push(s.id); s.kids.forEach((k) => ids.push(k.id)); });
    return ids;
  }, [tree]);

  const closeViewer = useCallback(() => {
    setViewer((v) => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);

  const onView = async (doc) => {
    if (loadingDoc) return;   // ignore a second click while one is in flight
    setLoadingDoc(doc.doc_id);
    try {
      // Two steps on purpose: mint a one-time link, then fetch it into a blob.
      // fetchAsBlobUrl sends no Authorization header, so hitting
      // /api/documents/{doc_id} directly returns 401 - and we never navigate to
      // the one-time URL itself, or the browser would burn it on a prefetch.
      const { url } = await getDocumentUrl({ docId: doc.doc_id }).unwrap();
      const blobUrl = await fetchAsBlobUrl(url);
      setViewer({ url: blobUrl, filename: doc.filename });
    } catch (e) {
      // Surfacing inline rather than a toast - the row is where the user looked.
      const detail = e?.data?.detail || e?.message || 'Could not open that document.';
      setViewer({ error: detail, filename: doc.filename });
    } finally {
      setLoadingDoc(null);
    }
  };

  const ctrlBtn = {
    border: '1px solid rgba(10,41,71,0.15)', background: 'white', borderRadius: 6,
    padding: '0.2rem 0.6rem', fontSize: '0.76rem', fontWeight: 600, color: brand.deepBlue,
    cursor: 'pointer',
  };

  if (!fscHouseholdId) {
    return (
      <p style={{ color: brand.charcoal, padding: '1rem', margin: 0 }}>
        Not linked to a Salesforce client yet.
      </p>
    );
  }

  return (
    <>
      <div style={{
        background: '#f7f8fa', borderBottom: '1px solid rgba(10,41,71,0.08)',
        padding: '0.7rem 1.25rem', display: 'flex', alignItems: 'center',
        gap: '0.6rem', flexWrap: 'wrap',
      }}>
        <i className="bi bi-diagram-3" style={{ color: brand.deepBlue }} />
        <span style={{ fontSize: '0.76rem', fontWeight: 800, color: brand.deepBlue, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Folder tree
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search documents…"
          style={{
            border: '1px solid rgba(10,41,71,0.2)', borderRadius: 6,
            padding: '0.3rem 0.6rem', fontSize: '0.84rem', flex: '1 1 220px', outline: 'none',
          }}
        />
        <button type="button" onClick={() => setOpen(new Set(allIds))} style={ctrlBtn}>
          <i className="bi bi-arrows-expand me-1" />Expand all
        </button>
        <button type="button" onClick={() => setOpen(new Set())} style={ctrlBtn}>
          <i className="bi bi-arrows-collapse me-1" />Collapse all
        </button>
        <span style={{ color: brand.charcoal, fontSize: '0.76rem', marginLeft: 'auto' }}>
          {filtered.length.toLocaleString()}
          {query ? ` of ${documents.length.toLocaleString()}` : ''} file
          {filtered.length === 1 ? '' : 's'}
        </span>
      </div>

      <div style={{ padding: isMobile ? '0.5rem' : '0.75rem 1.25rem' }}>
        {loadingDocs ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : docsError ? (
          // A 404 here means the household is outside the caller's offices or
          // not in the local FSC copy yet - not that it has no documents, which
          // is what an empty list would wrongly say.
          <p style={{ color: brand.charcoal, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>
            Could not load documents for this household. It may be outside the offices you can
            see, or newer than the last FSC Sync.
          </p>
        ) : filtered.length === 0 ? (
          <p style={{ color: brand.charcoal, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>
            {query
              ? `No documents match "${query}".`
              : (emptyMessage || 'No documents for this household.')}
          </p>
        ) : (
          tree.map((src) => (
            <div key={src.id}>
              <button
                type="button"
                onClick={() => toggle(src.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'none', border: 'none', padding: '0.35rem 0', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ width: 16, color: brand.charcoal }}>{isOpen(src.id) ? '−' : '+'}</span>
                <i className="bi bi-folder-fill" style={{ color: brand.goldenrod }} />
                <span style={{ fontWeight: 700, color: brand.deepBlue }}>{src.name}</span>
                <span style={{ color: brand.charcoal, fontSize: '0.78rem', marginLeft: 'auto' }}>
                  {src.count} file{src.count === 1 ? '' : 's'}
                </span>
              </button>

              {isOpen(src.id) && src.kids.map((kind) => (
                <div key={kind.id} style={{ marginLeft: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={() => toggle(kind.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'none', border: 'none', padding: '0.3rem 0', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span style={{ width: 16, color: brand.charcoal }}>{isOpen(kind.id) ? '−' : '+'}</span>
                    <i className="bi bi-folder" style={{ color: brand.goldenrod }} />
                    <span style={{ fontWeight: 600, color: brand.deepBlue }}>{kind.name}</span>
                    <span style={{ color: brand.charcoal, fontSize: '0.78rem', marginLeft: 'auto' }}>
                      {kind.count}
                    </span>
                  </button>

                  {isOpen(kind.id) && kind.files.map((doc) => (
                    <div
                      key={doc.doc_id}
                      style={{ marginLeft: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0', borderTop: '1px solid rgba(10,41,71,0.05)' }}
                    >
                      {loadingDoc === doc.doc_id ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          style={{ color: brand.tealBlue, width: 14, height: 14, borderWidth: 2 }}
                          role="status"
                          aria-label="Opening document"
                        />
                      ) : (
                        <i className="bi bi-file-earmark-text" style={{ color: brand.charcoal }} />
                      )}
                      <button
                        type="button"
                        onClick={() => onView(doc)}
                        disabled={Boolean(loadingDoc)}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          color: brand.tealBlue, textAlign: 'left', fontSize: '0.86rem',
                          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          // Only the other rows dim; the one being opened stays
                          // full strength so the eye follows it.
                          cursor: loadingDoc ? 'default' : 'pointer',
                          opacity: loadingDoc && loadingDoc !== doc.doc_id ? 0.55 : 1,
                        }}
                        title={doc.filename}
                      >
                        {doc.filename}
                      </button>
                      <span style={{ color: brand.charcoal, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                        {loadingDoc === doc.doc_id ? 'Opening…' : fmtSize(doc.content_size)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {viewer && !viewer.error && (
        <DocumentViewerModal url={viewer.url} filename={viewer.filename} onClose={closeViewer} />
      )}
      {viewer?.error && (
        <div style={{ margin: '0 1.25rem 0.75rem', color: '#c0392b', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle-fill me-1" />
          {viewer.filename}: {viewer.error}
          <button type="button" onClick={closeViewer} className="btn btn-sm btn-link">dismiss</button>
        </div>
      )}
    </>
  );
}
FscDocumentTree.propTypes = {
  // FSC household id (001Vz…). When absent the component says so rather than
  // rendering an empty tree, so callers can mount it unconditionally.
  fscHouseholdId: PropTypes.string,
  isMobile: PropTypes.bool,
  // Overrides the "no documents" line, so the hub side can say something that
  // makes sense there instead of "for this household".
  emptyMessage: PropTypes.string,
};
